import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Breadcrumb, MenuAccount, WideCardOrderDetail, Loading } from '../../components/Main'
import {
  InteractorOrderSearch,
  InteractorCsvOrder,
  InteractorCloseOrderCreate,
  InteractorCloseOrderCheck,
} from '../../interactors/Main'
import { useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { INVOICE_V2 } from '../../constant/global/config'
import { Modal } from 'react-responsive-modal'
import {
  closeOrderReasonMaxLength,
  closeOrderReasonMinLength,
} from '../../constant/global/validation'
import { confirmationAlert } from '../../utility/SweetAlert'
import moment from 'moment'
import InteractorShippingHeaderSearch from '../../interactors/transaction/shipping/InteractorShippingHeaderSearch'

const Order = () => {
  const { t } = useTranslation()
  const queryParams = new URLSearchParams(window.location.search)
  const configTime = moment(INVOICE_V2)
  const pathParts = window.location.pathname.split('/')
  const prevUrl = queryParams.get('prev')

  const uid = pathParts[pathParts.length - 1]
  const { account } = useSelector((state) => state.account)
  const location = useLocation()
  const { orderSearch } = InteractorOrderSearch()
  const { csvOrder } = InteractorCsvOrder()
  const { shippingHeaderSearch } = InteractorShippingHeaderSearch()
  const { closeOrderCreate } = InteractorCloseOrderCreate()
  const { closeOrderCheck } = InteractorCloseOrderCheck()
  const [loading, setLoading] = useState(true)
  const [loadingShipping, setLoadingShipping] = useState(true)
  const [loadingCheck, setLoadingCheck] = useState(false)
  const [modal, setModal] = useState(false)
  const [reason, setReason] = useState('')
  const [loadingCorrection, setLoadingCorrection] = useState(true)
  const [correctionInfo, setCorrectionInfo] = useState({
    hasRequest: false,
    count: 0,
    items: [],
  })

  const [download, setDownload] = useState({
    headers: null,
    items: null,
    filename: null,
  })
  const [datacard, setDatacard] = useState([])
  const [invoiceDate, setInvoiceDate] = useState(new Date())
  const invoiceVersionDate = new Date(INVOICE_V2)
  const [dataShipping, setDataShipping] = useState([])
  const [payload] = useState({
    page: 1,
    limit: 5,
    keywords: {
      text_search: queryParams.get('ref') ?? '',
      status: '',
      payment_type: '',
      account_uid: account.uid,
      store_uid: '',
      uid: location.pathname.split('/')[3],
    },
  })
  const [payloadShipping] = useState({
    page: 1,
    limit: 50,
    keywords: {
      text_search: '',
      uid: '',
      transaction_uid: uid,
      account_uid: account?.uid,
      status: '',
      store_uid: '',
    },
  })

  const handleResponse = useCallback(
    (data) => {
      setDatacard(data)
      setInvoiceDate(new Date(data?.items[0]?.created_date))
      csvOrder(data.items, setDownload)
    },
    [csvOrder],
  )

  const handleResponseShipping = useCallback((data) => setDataShipping(data), [])

  useEffect(() => {
    if (loading) {
      orderSearch(payload, handleResponse, setLoading)
    }
  }, [handleResponse, loading, payload, orderSearch])

  useEffect(() => {
    if (datacard?.items?.length > 0) {
    }
  }, [configTime, datacard])

  useEffect(() => {
    if (loadingShipping) {
      shippingHeaderSearch(payloadShipping, handleResponseShipping, setLoadingShipping)
    }
  }, [shippingHeaderSearch, handleResponseShipping, loadingShipping, payloadShipping])

  const checkBillStatus = JSON.parse(datacard?.items?.[0]?.bill ?? '[]').some((item) =>
    ['ISSUED', 'PROCESSED'].includes(item.status),
  )

  const hasShippedStatus =
    dataShipping &&
    dataShipping.items?.length > 0 &&
    dataShipping.items.some((item) => item.initial_status === 'SHIPPED')

  const countShippedStatus = useMemo(() => {
    return (
      dataShipping?.items?.filter((item) => ['SHIPPED'].includes(item.initial_status)).length ?? 0
    )
  }, [dataShipping])

  const showCorrectionButton = useMemo(() => {
    if (loadingCorrection) return false

    const isSettled = ['COMPLETED', 'CANCELLED', 'CLOSE_ORDER_REQUEST'].includes(
      datacard?.items?.[0]?.status,
    )

    const isOngoingStatusValid =
      !isSettled &&
      ((datacard?.items?.length > 0 && !hasShippedStatus && !checkBillStatus) ||
        (datacard?.items?.[0]?.bill === undefined && dataShipping?.items === null))

    const isSettledWithCorrection = isSettled && correctionInfo?.items?.length > 0

    return isOngoingStatusValid || isSettledWithCorrection
  }, [
    loadingCorrection,
    datacard?.items,
    hasShippedStatus,
    checkBillStatus,
    dataShipping,
    correctionInfo?.items,
  ])

  const handleCloseReason = () => {
    setReason('')
    setModal(false)
  }

  const handleSubmitClosePoReason = async () => {
    const confirm = await confirmationAlert({
      title: t('title.are_you_sure'),
      message: t('message.confirmation_close_order'),
      icon: 'warning',
    })

    if (confirm) {
      setModal(false)
      const payload = {
        uid: uid,
        status: 'CLOSE_ORDER_REQUEST',
        close_order_reason: reason,
      }
      closeOrderCreate(payload)
    }
  }

  const handleCloseOrder = async () => {
    setReason('')
    setLoadingCheck(true)
    const payload = { uid: uid }
    closeOrderCheck(payload, setLoadingCheck, setModal)
  }

  return (
    <>
      {loadingCheck && (
        <div className="d-flex justify-content-center">
          <Loading type="overlay" />
        </div>
      )}

      {/* Close order reason modal */}
      <Modal
        open={modal}
        onClose={handleCloseReason}
        classNames={{
          modal: 'col-5',
          modalContainer: 'row justify-content-center align-items-center',
        }}
      >
        <div className="container-fluid p-0">
          <div className="border-bottom pb-2 mb-3">
            <h5 className="font-weight-bold mb-0">{t('title.close_order')}</h5>
          </div>

          <div className="form-group">
            <label className="font-weight-semibold">{t('title.close_order_reason')}</label>
            <textarea
              className="form-control"
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              minLength={closeOrderReasonMinLength}
              maxLength={closeOrderReasonMaxLength}
            />
            <small className="form-text d-block text-right text-secondary">
              {reason.length}/{closeOrderReasonMaxLength} karakter
            </small>
          </div>

          <div className="d-flex justify-content-end gap-2 pt-3 border-top">
            <button
              type="button"
              className="btn btn-light border border-gray-5 mr-2"
              onClick={handleCloseReason}
            >
              {t('button.cancel')}
            </button>

            <button
              type="submit"
              className="btn btn-primary text-white"
              disabled={!reason.length || reason.length < closeOrderReasonMinLength}
              onClick={handleSubmitClosePoReason}
            >
              {t('button.save')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Detail content */}
      <Breadcrumb title={t('menu.order')} className="mb-4" />
      <div className="container">
        <div className="row mb-8">
          <MenuAccount />
          <div className="col-xl-9 col-wd-9gdot5">
            <div className="flex-center-between mt-2 mt-0-lg flex-column flex-md-row align-items-start align-md-items-center">
              <h3 className="font-size-20 font-size-20-lg font-weight-bold mb-0">
                {t('menu.order_view')}
              </h3>

              <div>
                {showCorrectionButton && (
                  <>
                    <button
                      onClick={() => {
                        window.location = `/order/correction/${uid}`
                      }}
                      className="position-relative text-light btn-danger btn-outline-secondary btn btn-xs mx-1 my-2 pointer font-size-12"
                    >
                      {t('button.order_correction')}
                      {correctionInfo?.hasRequest && (
                        <span
                          className="position-absolute d-inline-flex top-0 start-100 translate-middle align-items-center justify-content-center btn-dark rounded-circle font-weight-bold"
                          style={{
                            minWidth: 22,
                            height: 22,
                            fontSize: 12,
                            transform: 'translate(-5%, -55%)',
                          }}
                        >
                          {correctionInfo?.count}
                        </span>
                      )}
                    </button>
                  </>
                )}

                {datacard?.items?.length > 0 &&
                  invoiceDate &&
                  invoiceDate > invoiceVersionDate &&
                  INVOICE_V2 && ['support+buyer@adaloka.com', 'sandbox.buyer@wdn.co.id'] &&
                  moment(datacard.items[0].delivery_estimated_time).isAfter(configTime) && (
                    <Link
                      to={'#'}
                      onClick={() => {
                        window.location = `/order/shipping/${uid}`
                      }}
                      className="position-relative text-light btn-primary btn-outline-secondary btn btn-xs mx-1 my-2 font-size-12"
                    >
                      {t('button.order_shipping')}
                      {hasShippedStatus && (
                        <span
                          className="position-absolute d-inline-flex top-0 start-100 translate-middle align-items-center justify-content-center btn-dark rounded-circle font-weight-bold"
                          style={{
                            minWidth: 22,
                            height: 22,
                            fontSize: 12,
                            transform: 'translate(-5%, -55%)',
                          }}
                        >
                          {countShippedStatus}
                        </span>
                      )}
                    </Link>
                  )}

                <button
                  onClick={() => {
                    window.location = prevUrl ? `/${prevUrl}` : `/order`
                  }}
                  className="text-dark btn-outline-secondary btn btn-xs mx-1 my-2 font-size-12 pointer"
                >
                  <i className="fas fa-arrow-left px-2"></i> {t('button.back')}
                </button>
              </div>
            </div>
            <hr />
            <WideCardOrderDetail
              loading={loading}
              datacard={datacard}
              payload={payload}
              download={download}
              onCloseOrder={handleCloseOrder}
              onCorrectionChange={(data) => {
                setCorrectionInfo(data)
                setLoadingCorrection(false)
              }}
              correctionInfo={correctionInfo}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default Order
